import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import {
  RefreshCw, Loader2, CheckCircle2, XCircle, ArrowRight, Sparkles,
} from 'lucide-react';
import { refreshESignStatuses } from '../api';

const STATUS_TONE = {
  SENT: 'bg-blue-50 text-blue-700 border-blue-200',
  VIEWED: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  SIGNED: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  DECLINED: 'bg-red-50 text-red-700 border-red-200',
  EXPIRED: 'bg-orange-50 text-orange-700 border-orange-200',
  RECALLED: 'bg-zinc-100 text-zinc-700 border-zinc-300',
  DRAFT: 'bg-amber-50 text-amber-700 border-amber-200',
};

export default function ZohoSignBulkRefreshDialog({ open, onOpenChange, inProgressCount = 0, onComplete }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null); // { polled, total, batch, totalBatches }

  const CHUNK_SIZE = 50;

  const handleRun = async () => {
    setRunning(true);
    setResult(null);
    setProgress(null);

    // Accumulate results across chunks so the user sees the final
    // aggregated outcome even when polling 100+ offers (we paginate the
    // server endpoint to stay under the K8s 120s ingress timeout).
    const aggregate = {
      total_polled: 0,
      changed: 0,
      unchanged: 0,
      failed: 0,
      results: [],
    };

    try {
      let offset = 0;
      let totalInProgress = inProgressCount || 0;
      let batch = 0;
      let totalBatches = Math.max(1, Math.ceil((totalInProgress || CHUNK_SIZE) / CHUNK_SIZE));

      // Loop with a hard guard against runaway iteration.
      while (batch < 200) {
        batch += 1;
        setProgress({
          polled: aggregate.total_polled,
          total: totalInProgress,
          batch,
          totalBatches,
        });

        const { data } = await refreshESignStatuses({
          limit: CHUNK_SIZE,
          offset,
        });

        aggregate.total_polled += data.total_polled || 0;
        aggregate.changed += data.changed || 0;
        aggregate.unchanged += data.unchanged || 0;
        aggregate.failed += data.failed || 0;
        if (Array.isArray(data.results)) aggregate.results.push(...data.results);

        // Re-derive total from server (more authoritative than prop).
        if (typeof data.total_in_progress === 'number') {
          totalInProgress = data.total_in_progress;
          totalBatches = Math.max(1, Math.ceil(totalInProgress / CHUNK_SIZE));
        }

        if (!data.has_more || (data.total_polled || 0) === 0) break;
        offset += CHUNK_SIZE;
      }

      setResult(aggregate);
      if (aggregate.changed > 0) {
        toast.success(`${aggregate.changed} status change(s) detected`);
      } else if (aggregate.total_polled === 0) {
        toast.info('No in-progress requests to refresh');
      } else {
        toast.info('All statuses are up to date');
      }
      if (aggregate.failed > 0) {
        toast.error(`${aggregate.failed} request(s) failed to refresh`, { duration: 6000 });
      }
      if (onComplete) onComplete(aggregate);
    } catch (e) {
      // Preserve any partial progress in the UI so the user knows what
      // already got processed before the batch that errored.
      if (aggregate.total_polled > 0) {
        setResult(aggregate);
        toast.error(
          `Bulk refresh stopped after ${aggregate.total_polled} of ${inProgressCount}. ${e?.response?.data?.detail || e?.message || 'Network error'}`,
          { duration: 8000 },
        );
      } else {
        toast.error(e?.response?.data?.detail || e?.message || 'Bulk refresh failed');
      }
    } finally {
      setRunning(false);
      setProgress(null);
    }
  };

  const handleClose = () => {
    if (running) return;
    setResult(null);
    setProgress(null);
    onOpenChange(false);
  };

  const changedRows = (result?.results || []).filter((r) => r.changed);
  const failedRows = (result?.results || []).filter((r) => !r.success);

  return (
    <Dialog open={open} onOpenChange={(v) => !running && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="bulk-refresh-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw size={20} className="text-cyan-600" />
            Bulk Refresh E-Sign Statuses
          </DialogTitle>
          <DialogDescription>
            Poll Zoho Sign for every in-progress request and surface any signed / declined / expired updates without waiting for webhooks.
          </DialogDescription>
        </DialogHeader>

        {/* Pre-run pane */}
        {!result && !running && (
          <div className="rounded-lg border bg-slate-50 p-5 text-center">
            <div className="text-4xl font-bold text-slate-900">{inProgressCount}</div>
            <div className="text-xs uppercase tracking-wide text-slate-500 mt-1 mb-3">
              in-progress request(s) will be polled
            </div>
            <p className="text-xs text-slate-600 max-w-md mx-auto">
              We&apos;ll fetch the latest status from Zoho Sign for each <Badge className="text-[10px]">SENT</Badge>,{' '}
              <Badge className="text-[10px]">VIEWED</Badge>, and <Badge className="text-[10px]">DRAFT</Badge> request, update the local record, and log everything to the audit trail.
            </p>
            {inProgressCount === 0 && (
              <p className="mt-3 text-emerald-700 text-xs font-medium">
                🎉 Nothing to refresh — all e-sign requests are settled.
              </p>
            )}
          </div>
        )}

        {/* Running state */}
        {running && (
          <div className="rounded-lg border bg-slate-50 p-8 text-center space-y-3" data-testid="refresh-running-pane">
            <Loader2 size={36} className="mx-auto animate-spin text-cyan-600" />
            <div className="text-sm font-medium text-slate-700">
              {progress
                ? `Polling batch ${progress.batch} of ${progress.totalBatches}…`
                : `Polling Zoho Sign for ${inProgressCount} request(s)…`}
            </div>
            {progress && progress.total > 0 && (
              <div className="space-y-1.5">
                <div className="text-xs text-slate-500">
                  {progress.polled} of {progress.total} polled
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-cyan-600 h-2 transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.round((progress.polled / progress.total) * 100))}%` }}
                    data-testid="refresh-progress-bar"
                  />
                </div>
              </div>
            )}
            <div className="text-xs text-slate-500">
              Batched in groups of 50 to stay under timeout limits.
            </div>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <Stat label="Polled" value={result.total_polled} color="text-slate-700" testid="refresh-polled" />
              <Stat label="Changed" value={result.changed} color="text-emerald-600" testid="refresh-changed" />
              <Stat label="Unchanged" value={result.unchanged} color="text-slate-500" testid="refresh-unchanged" />
              <Stat label="Failed" value={result.failed} color="text-red-600" testid="refresh-failed" />
            </div>

            {changedRows.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-emerald-700 flex items-center gap-2">
                  <Sparkles size={14} /> {changedRows.length} status change(s) detected
                </h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-emerald-50">
                      <tr>
                        <th className="text-left p-2 font-medium">Candidate</th>
                        <th className="text-left p-2 font-medium">Was</th>
                        <th className="text-center p-2 font-medium"></th>
                        <th className="text-left p-2 font-medium">Now</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changedRows.map((r, idx) => (
                        <tr key={r.offer_id || idx} className="border-t" data-testid={`refresh-changed-row-${idx}`}>
                          <td className="p-2 font-medium">{r.candidate_name || '—'}</td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[10px] ${STATUS_TONE[r.previous_status] || ''}`}>
                              {r.previous_status || '—'}
                            </Badge>
                          </td>
                          <td className="p-2 text-center text-slate-400">
                            <ArrowRight size={14} className="inline" />
                          </td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[10px] font-semibold ${STATUS_TONE[r.new_status] || ''}`}>
                              {r.new_status || '—'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {failedRows.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-red-700 flex items-center gap-2">
                  <XCircle size={14} /> {failedRows.length} failed
                </h4>
                <div className="border rounded-lg overflow-hidden max-h-32 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {failedRows.map((r, idx) => (
                        <tr key={r.offer_id || idx} className="border-t">
                          <td className="p-2 font-medium">{r.candidate_name || '—'}</td>
                          <td className="p-2 text-slate-500 truncate">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {changedRows.length === 0 && failedRows.length === 0 && result.total_polled > 0 && (
              <div className="text-center py-6 text-slate-500 text-sm">
                <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
                All {result.total_polled} statuses are up to date — no changes detected.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={running} data-testid="refresh-close-btn">
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!result && (
            <Button
              onClick={handleRun}
              disabled={running || inProgressCount === 0}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="refresh-run-btn"
            >
              {running ? (
                <>
                  <Loader2 className="animate-spin mr-1.5" size={16} />
                  Polling…
                </>
              ) : (
                <>
                  <RefreshCw size={16} className="mr-1.5" />
                  Refresh {inProgressCount} now
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, color = 'text-slate-900', testid }) {
  return (
    <div className="rounded-lg border bg-slate-50 px-3 py-2 text-center">
      <div className={`text-2xl font-bold ${color}`} data-testid={testid}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
